import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Button, Checkbox, Space, Spin } from 'antd';
import {
  SyncOutlined,
  UserOutlined,
  ShoppingOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import localforage from 'localforage';
import moment from 'moment';
import { useLocalForageContext, useAuthContext, useClientConfig } from '../context';
import { sendSyncDiagnostics } from '../utils/sentryHelpers';
import NetworkQualityBanner from './NetworkQualityBanner';
import { useTranslation } from '../localization';
import { syncNotificationsManually } from '../notifications/bridge';
import { getSyncableKeys } from '../utils/syncSelection';

const stores = {
  products: localforage.createInstance({ name: 'Products' }),
  tasks: localforage.createInstance({ name: 'Tasks' }),
  customers: localforage.createInstance({ name: 'Customers' }),
  reports: localforage.createInstance({ name: 'Reports' }),
  openItems: localforage.createInstance({ name: 'OpenItems' }),
};

const formatSyncTime = (duration) => {
  if (!duration) return null;
  const totalMins = Math.floor(duration.asMinutes());
  if (totalMins < 0) return null;
  if (totalMins >= 1440) {
    const days = Math.floor(totalMins / 1440);
    return `${days}d`;
  }
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return 'Sada';
};

const SYNC_OPTIONS = [
  { key: 'products', label: 'Artikli', icon: ShoppingOutlined },
  { key: 'tasks', label: 'Zadaci', icon: CheckSquareOutlined },
  { key: 'customers', label: 'Kupci', icon: UserOutlined },
  { key: 'reports', label: 'Izvještaji', icon: FileTextOutlined },
  { key: 'openItems', label: 'Otvorene stavke', icon: UnorderedListOutlined },
];

const getDefaultSelectedOptions = (excludedOptions = []) =>
  SYNC_OPTIONS
    .filter(({ key }) => !excludedOptions.includes(key))
    .map(({ key }) => key);

const SyncDialog = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const { getValue } = useClientConfig();
  const excludedOptions = getValue('sync.defaultExcludedOptions', []);
  const defaultSelectedOptions = useMemo(
    () => getDefaultSelectedOptions(excludedOptions),
    [excludedOptions],
  );
  const [selected, setSelected] = useState(defaultSelectedOptions);
  const [syncTimes, setSyncTimes] = useState({});
  const [itemCounts, setItemCounts] = useState({});

  useEffect(() => {
    if (visible) {
      setSelected(defaultSelectedOptions);

      const loadSyncTimes = async () => {
        const now = moment();
        const times = {};
        const counts = {};

        await Promise.all(
          SYNC_OPTIONS.map(async ({ key }) => {
            const createdAt = await stores[key].getItem('createdAt');
            if (createdAt) {
              const syncMoment = moment(createdAt, 'DD/MM/YYYY HH:mm:ss');
              times[key] = moment.duration(now.diff(syncMoment));
            }

            if (key === 'products') {
              const meta = await stores[key].getItem('products_meta');
              if (meta?.count != null && meta?.posCount != null) {
                counts[key] = meta.count;
                counts.products_pos = meta.posCount;
              } else {
                const { compressedStorage } = await import('../utils/compressedStorage');
                const products = await compressedStorage.load(stores[key], 'products');
                if (Array.isArray(products)) {
                  counts[key] = products.length;
                  counts.products_pos = products.filter(p => p.isPos).length;
                }
              }
            } else if (key === 'reports') {
              const nodes = await stores[key].getItem('ordersNodes');
              if (Array.isArray(nodes)) counts[key] = nodes.length;
            } else {
              const nodes = await stores[key].getItem('nodes');
              if (Array.isArray(nodes)) counts[key] = nodes.length;
            }
          })
        );

        setSyncTimes(times);
        setItemCounts(counts);
      };

      loadSyncTimes();
    }
  }, [visible, defaultSelectedOptions]);

  const {
    loadingCustomers,
    loadingProducts,
    loadingTasks,
    loadingReports,
    loadingOpenItems,
    setRefetchCustomers,
    setRefetchProducts,
    setRefetchTasks,
    setRefetchReports,
    setRefetchOpenItems,
    setRefetchOrders,
  } = useLocalForageContext();

  const { authUser } = useAuthContext();

  const loadingMap = useMemo(() => ({
    customers: loadingCustomers,
    products: loadingProducts,
    tasks: loadingTasks,
    reports: loadingReports,
    openItems: loadingOpenItems,
  }), [loadingCustomers, loadingProducts, loadingTasks, loadingReports, loadingOpenItems]);

  const canSyncSelected = getSyncableKeys(selected, loadingMap).length > 0;
  const canSyncAll = getSyncableKeys(defaultSelectedOptions, loadingMap).length > 0;

  const handleSync = useCallback((keys) => {
    syncNotificationsManually().catch(() => { /* Module exposes its error and preserves pending intents. */ });
    sendSyncDiagnostics(keys, authUser);
    if (keys.includes('customers') && !loadingCustomers) setRefetchCustomers(true);
    if (keys.includes('products') && !loadingProducts) setRefetchProducts(true);
    if (keys.includes('tasks') && !loadingTasks) setRefetchTasks(true);
    if (keys.includes('reports') && !loadingReports) setRefetchReports(true);
    if (keys.includes('openItems') && !loadingOpenItems) setRefetchOpenItems(true);
    setRefetchOrders(true);
    onClose();
  }, [
    authUser,
    loadingCustomers, loadingProducts, loadingTasks, loadingReports, loadingOpenItems,
    setRefetchCustomers, setRefetchProducts, setRefetchTasks, setRefetchReports, setRefetchOpenItems,
    setRefetchOrders, onClose,
  ]);

  const handleSyncSelected = useCallback(() => {
    if (selected.length === 0) return;
    handleSync(selected);
  }, [selected, handleSync]);

  const handleSyncAll = useCallback(() => {
    handleSync(defaultSelectedOptions);
  }, [defaultSelectedOptions, handleSync]);

  const handleQuickSync = useCallback((key) => {
    handleSync([key]);
  }, [handleSync]);

  const toggleOption = useCallback((key) => {
    setSelected(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  }, []);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <SyncOutlined /> {t('sync.incomingTitle')}
        </span>
      }
      footer={null}
      centered
      width={340}
      style={{ maxWidth: 'calc(100vw - 16px)' }}
      bodyStyle={{ padding: '16px 20px' }}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Brza sinhronizacija:
        </div>
        <Space wrap>
          <Button
            size="small"
            icon={loadingMap.products ? <Spin size="small" /> : <ShoppingOutlined />}
            onClick={() => handleQuickSync('products')}
            disabled={loadingMap.products}
          >
            Artikli
          </Button>
          <Button
            size="small"
            icon={loadingMap.tasks ? <Spin size="small" /> : <CheckSquareOutlined />}
            onClick={() => handleQuickSync('tasks')}
            disabled={loadingMap.tasks}
          >
            Zadaci
          </Button>
        </Space>
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Odaberi šta sinhronizovati:
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {SYNC_OPTIONS.map(({ key, label, icon: Icon }) => {
            const syncTime = syncTimes[key];
            const formattedTime = formatSyncTime(syncTime);
            const mins = syncTime ? Math.floor(syncTime.asMinutes()) : 0;
            const timeColor = mins >= 60 ? '#ff4d4f' : mins >= 20 ? '#faad14' : '#52c41a';

            const isSelected = selected.includes(key);
            const isLoading = loadingMap[key];
            const count = itemCounts[key];
            const posCount = key === 'products' ? itemCounts.products_pos : null;

            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: isSelected ? '#f6ffed' : '#fafafa',
                  borderRadius: 6,
                  border: `1px solid ${isSelected ? '#b7eb8f' : '#f0f0f0'}`,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
                onClick={() => !isLoading && toggleOption(key)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isLoading ? <Spin size="small" /> : <Icon style={{ color: '#1890ff' }} />}
                  <span>
                    {label}
                    {count != null && (
                      <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>
                        {count.toLocaleString()}
                        {posCount != null && ` · POS: ${posCount.toLocaleString()}`}
                      </span>
                    )}
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {formattedTime && (
                    <span style={{ fontSize: 12, color: timeColor, fontWeight: 500 }}>
                      {formattedTime}
                    </span>
                  )}
                  <Checkbox
                    checked={isSelected}
                    disabled={isLoading}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleOption(key)}
                  />
                </span>
              </div>
            );
          })}
        </Space>
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginBottom: 12, textAlign: 'center' }}>
        <NetworkQualityBanner visible={visible} />
      </div>

      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={handleSyncSelected}
          disabled={!canSyncSelected}
          style={{ flex: 1 }}
        >
          Sinhronizuj odabrano ({selected.length})
        </Button>
        <Button
          onClick={handleSyncAll}
          disabled={!canSyncAll}
        >
          Sve
        </Button>
      </Space>
    </Modal>
  );
};

export default SyncDialog;
